import { useEffect, useMemo } from 'react';

import { Table } from '@tanstack/react-table';
import { Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SelectionActionButton, SelectionActions } from '@/components/ui/table/table-toolbar';
import { trackEvent } from '@/segment/segment';
import { Campaign } from '@/shared/types/campaigns';
import { EventName } from '@/shared/types/segment';

interface CampaignsSelectionActionsProps {
  table: Table<Campaign>;
  /** The campaigns currently rendered (one page worth). */
  data: Campaign[];
  /** Changes whenever a filter, the search term or the sort changes. */
  selectionResetKey?: string;
  currentPageIndex: number;
  onDelete: (campaigns: Campaign[]) => void;
}

export const CampaignsSelectionActions = ({
  table,
  data,
  selectionResetKey,
  currentPageIndex,
  onDelete,
}: CampaignsSelectionActionsProps) => {
  const { t } = useTranslation();

  // Selection is page-scoped: a new filter, search term, sort or page drops it wholesale.
  // `selectionResetKey` also covers the `keepPreviousData` window, where the previous rows stay
  // on screen until the refetch resolves.
  useEffect(() => {
    table.resetRowSelection();
  }, [selectionResetKey, currentPageIndex, table]);

  // `data` is deliberately not a dependency above. `campaigns_updated` is broadcast to everyone
  // (useRtmSetup does not filter it by user) and `onCampaignUpdated` rebuilds the cached page
  // array on every status or counter tick, so resetting on its identity would wipe the selection
  // under a user who is still picking rows. Rows do leave the selection's reach without the query
  // changing though — deleted here or elsewhere, or moved to in_progress, which the server
  // refuses to delete — so drop just those ids and keep the rest of the picks.
  useEffect(() => {
    const selectableIds = new Set(
      table
        .getRowModel()
        .rows.filter((row) => row.getCanSelect())
        .map((row) => row.id)
    );
    const selectedIds = Object.keys(table.getState().rowSelection);
    if (selectedIds.every((id) => selectableIds.has(id))) {
      return;
    }
    table.setRowSelection((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([id]) => selectableIds.has(id)))
    );
  }, [data, table]);

  // Read selection from state and intersect with the rows actually on screen: the state can still
  // hold ids from a page we have navigated away from.
  const rowSelection = table.getState().rowSelection;
  const selectedCampaigns = useMemo(
    () => data.filter((campaign) => rowSelection[campaign.campaign_id]),
    [data, rowSelection]
  );

  const handleDelete = () => {
    trackEvent(EventName.ClickBulkDeleteCampaigns, {
      campaign_ids: selectedCampaigns.map((campaign) => campaign.campaign_id),
      selected_count: selectedCampaigns.length,
    });
    onDelete(selectedCampaigns);
  };

  return (
    <SelectionActions
      count={selectedCampaigns.length}
      getLabel={(count) => t('campaigns.selected', { count })}
      onDeselectAll={() => table.resetRowSelection()}
    >
      <SelectionActionButton
        label={t('common.delete')}
        className="hover:text-destructive hover:[&_svg]:text-destructive"
        onClick={handleDelete}
      >
        <Trash2Icon />
      </SelectionActionButton>
    </SelectionActions>
  );
};
